import { useDispatch, useSelector } from "react-redux";

import type {
    BaseActivatedPanelProps,
    SelectionDropdownItem,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { ManualSelectionItem } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import {
    selectActiveLeadsType,
    selectSelectedRowDomains,
    setIsRowSelectionEnabled,
    useClearAndCloseRowsSelection,
} from "app/pages/sales-intelligence/modules/leads-search";
import { useListParams } from "app/pages/sales-intelligence/modules/lists/context/ListParamsContext";
import ActivatedRemoveFromListPanelTracked from "app/pages/sales-intelligence/sub-modules/opportunities/containers/ActivatedRemoveFromListPanel/ActivatedRemoveFromListPanelTracked";

import { useLeadsResultsActionsState } from "../../../hooks/useLeadsResultsActionsState";
import { useRemoveLeadsFromListMutation } from "../../../query/mutations/useRemoveLeadsFromListMutation";
import { useRemoveTopNLeadsFromListMutation } from "../../../query/mutations/useRemoveTopNLeadsFromListMutation";

const RemoveFromListPanelContainer = (props: BaseActivatedPanelProps) => {
    const dispatch = useDispatch();

    const listParams = useListParams();

    const selectedRowDomains = useSelector(selectSelectedRowDomains);
    const activeLeadsType = useSelector(selectActiveLeadsType);

    const { areResultsFetching, areActionsDisabled, numberOfResults } = useLeadsResultsActionsState(activeLeadsType);

    const clearAndCloseRowsSelection = useClearAndCloseRowsSelection();

    const handleDeactivate = (): void => {
        clearAndCloseRowsSelection();
        props.onDeactivate();
    };

    const removeLeadsMutation = useRemoveLeadsFromListMutation({
        onSuccess: handleDeactivate,
    });
    const removeTopNLeadsMutation = useRemoveTopNLeadsFromListMutation(activeLeadsType, {
        onSuccess: handleDeactivate,
    });

    const handleSelectionChange = (item: Readonly<SelectionDropdownItem>): void => {
        if (item.id === ManualSelectionItem.ID) {
            dispatch(setIsRowSelectionEnabled(true));

            return;
        }

        clearAndCloseRowsSelection();
    };

    const handleSubmit = (item: Readonly<SelectionDropdownItem>) => {
        if (item.id === ManualSelectionItem.ID) {
            removeLeadsMutation.mutate({
                listParams,
                domains: selectedRowDomains,
                includeRegionals: activeLeadsType === "companies",
            });

            return;
        }

        removeTopNLeadsMutation.mutate({
            listParams,
            domainsCount: item.value,
            includeRegionals: activeLeadsType === "companies",
        });
    };

    return (
        <ActivatedRemoveFromListPanelTracked
            selectedIds={selectedRowDomains}
            numberOfResults={numberOfResults}
            isLoading={areResultsFetching || removeLeadsMutation.isPending || removeTopNLeadsMutation.isPending}
            isDisabled={areActionsDisabled || removeLeadsMutation.isPending || removeTopNLeadsMutation.isPending}
            onSubmit={handleSubmit}
            onDeactivate={handleDeactivate}
            onSelectionChange={handleSelectionChange}
        />
    );
};

export default RemoveFromListPanelContainer;
